import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { EMPTY_VALUE, TAKA_SYMBOL } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import type { Batch } from '../types/publish.types';
import { formatBatchAmount } from '../utils/formatBatchAmount';

interface BatchActionDialogProps {
  batch: Batch | null;
  title: string;
  description: string;
  confirmLabel: string;
  commentRequired?: boolean;
  destructive?: boolean;
  isPending: boolean;
  onConfirm: (comments: string) => void;
  onOpenChange: (open: boolean) => void;
}

export function BatchActionDialog({
  batch,
  title,
  description,
  confirmLabel,
  commentRequired = false,
  destructive = false,
  isPending,
  onConfirm,
  onOpenChange,
}: BatchActionDialogProps) {
  const key = batch?.batchId ?? null;
  const [draft, setDraft] = useState<{ key: number | null; text: string }>({
    key,
    text: '',
  });
  const comments = draft.key === key ? draft.text : '';

  const blocked = commentRequired && comments.trim() === '';

  const covering =
    batch?.trnDateFrom == null
      ? EMPTY_VALUE
      : `${formatDisplayDate(batch.trnDateFrom)} - ${formatDisplayDate(batch.trnDateTo)}`;

  return (
    <Dialog open={batch !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {batch === null ? null : (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border bg-muted/40 p-3 text-sm sm:grid-cols-4">
            <SummaryItem label="Batch" value={`#${String(batch.legacyId)}`} />
            <SummaryItem
              label="Amount"
              value={`${TAKA_SYMBOL} ${formatBatchAmount(batch.totalAmount)}`}
            />
            <SummaryItem
              label="ZMs"
              value={batch.rowsArchived ? 'archived' : formatBatchAmount(batch.memberCount)}
            />
            <SummaryItem label="Covering" value={covering} />
          </dl>
        )}

        <div>
          <p className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            Comment{commentRequired ? '' : ' (optional)'}
          </p>
          <Input
            value={comments}
            onChange={(event) => setDraft({ key, text: event.target.value })}
            placeholder="Why, in a line"
            autoComplete="off"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={destructive ? 'destructive' : 'default'}
            disabled={blocked || isPending}
            onClick={() => onConfirm(comments.trim())}
          >
            {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface SummaryItemProps {
  label: string;
  value: string;
}

function SummaryItem({ label, value }: SummaryItemProps) {
  return (
    <div className="grid gap-0.5">
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}
